import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import {
    EventWithMembership,
    getAthletesForEvent,
    listenToRegisteredAthletesChanges,
} from '@modules/supabase/events';
import { EVENT_ROLE, RegisteredEvent, RelatedAthlete, STATUS } from '@type/db-types';
import MaterialTable, { materialTableHeight } from '@components/molecules/MaterialTable';
import { Column, Options } from '@material-table/core';
import { getRelatedAthletes } from '@modules/supabase/users';
import ClassSelector from '@atoms/ClassSelector';
import DivisionSelector from '@atoms/DivisionSelector';
import { userColumns } from '@utils/user-table-utils';
import { GENDER_LOOKUP } from '@utils/team-table-utils';
import PaymentStatusSelector from '@atoms/PaymentStatusSelector';

interface Props {
    adminView: boolean; // admin/manage mode only
    event: EventWithMembership;
    onClose?: () => void;
    title?: string;
    statuses: STATUS[];
}

export default function RegisteredAthletesTable({
    adminView,
    event,
    onClose,
    statuses,
    title,
}: Props) {
    const [athletes, setAthletes] = useState<RegisteredEvent[] | null>(null);
    const [relatedAthletes, setRelatedAthletes] = useState<RelatedAthlete[] | null>(null);
    const tableRef: React.MutableRefObject<undefined | unknown> = useRef();

    const refreshUsers = useCallback(
        () =>
            getAthletesForEvent(event.id, [EVENT_ROLE.PARTICIPANT], statuses).then((a) => {
                setAthletes(a);
            }),
        [event.id, statuses],
    );

    // Listen to registration changes so users who are signing up are updated in real-time
    useEffect(() => {
        if (event?.id) return listenToRegisteredAthletesChanges(event.id, refreshUsers, statuses);
    }, [event.id, refreshUsers, statuses]);

    useEffect(() => {
        refreshUsers();
        getRelatedAthletes().then(setRelatedAthletes);
    }, [event.id, refreshUsers]);

    const tableColumns = useMemo(() => {
        return getTableColumns(
            event,
            adminView
                ? athletes?.map((a) => a.athleteId) || []
                : relatedAthletes
                  ? relatedAthletes.map((a) => a.athleteId)
                  : [],
            refreshUsers,
            adminView,
        );
    }, [adminView, athletes, event, refreshUsers, relatedAthletes]);

    const options = useTableOptions(adminView);

    return (
        <>
            <MaterialTable<RegisteredEvent>
                tableRef={tableRef}
                // actions={actions}
                columns={tableColumns}
                data={athletes || []}
                loading={athletes === null}
                localization={{
                    toolbar: {
                        nRowsSelected: (number) =>
                            `${number} wrestler${number === 1 ? '' : 's'} selected`,
                    },
                }}
                options={options}
                style={{ boxShadow: 'none' }}
                tableId="Registered-Wrestlers"
                title={title}
            />
            {onClose ? (
                <div className="d-flex justify-content-end">
                    <div className="d-flex gap-3 mt-3 w-50">
                        <Button
                            type="primary"
                            htmlType="button"
                            className="w-100 btn-outline rounded-pill"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            ) : null}
        </>
    );
}

const getTableColumns = (
    event: EventWithMembership,
    editableUsers: string[],
    refreshUsers: () => void,
    editable: boolean,
    // onPressEdit: (data: RegisteredEvent) => void,
): Column<RegisteredEvent>[] => [
    ...(userColumns('athleteId', editable, (userId: string) =>
        editableUsers.includes(userId),
    ) as unknown as Column<RegisteredEvent>[]),
    {
        title: 'Division',
        field: 'divisionName',
        render: (athlete) =>
            editableUsers.includes(athlete.athleteId) ? (
                <DivisionSelector registrant={athlete} refresh={refreshUsers} />
            ) : (
                athlete.divisionName || 'n/a'
            ),
    },
    {
        title: 'Class',
        field: 'className',
        render: (athlete) => (
            <ClassSelector
                registrant={athlete}
                refresh={refreshUsers}
                enabled={editableUsers.includes(athlete.athleteId)}
            />
        ),
    },
    {
        field: 'gender',
        lookup: GENDER_LOOKUP,
        title: 'Gender',
        width: '4rem',
    },
    ...(editable
        ? // hide payments, CSV export, and email for non-admins
          ([
              { title: 'Role', field: 'role', hidden: true },
              {
                  title: 'Payment',
                  field: 'paymentStatus',
                  render: (athlete) => (
                      <PaymentStatusSelector registrant={athlete} refresh={refreshUsers} />
                  ),
              },
          ] as Column<RegisteredEvent>[])
        : []),
];

const useTableOptions = (isAdmin: boolean): Options<RegisteredEvent> => {
    return useMemo(
        () => ({
            actionsColumnIndex: -1,
            maxBodyHeight: `${materialTableHeight - 21}vh`,
            minBodyHeight: `${materialTableHeight - 21}vh`,
            ...(isAdmin ? {} : { exportMenu: [] }),
        }),
        [isAdmin],
    );
};
